'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { ReactNode, useState } from 'react';

import { useV1Currencies } from '@flipster/features/src/shared/currency/use-v1-currencies';
import { Button, RadioButton } from '@flipster/react/design-system';
import { Modal } from '@flipster/react/design-system/modal';
import Formatted from '@flipster/react/design-utils/formatted';
import { walletsBunja } from '@flipster/socket/r230522/wallet';
import { css } from '@flipster/styled-system/css';
import { vstack } from '@flipster/styled-system/patterns';
import Num from '@flipster/utils/num';

import { useWalletModal } from '@web/app/[locale]/(web)/_modal/WalletModal/hooks/useWalletModal';

import { useLaunchpoolById } from '../_hooks/use-launchpool-by-id';

type SelectActionType = 'deposit' | 'convert';

const ActionRadioItems: { label: ReactNode; value: SelectActionType }[] = [
  { label: 'Deposit', value: 'deposit' },
  { label: 'Convert', value: 'convert' },
];

interface Props {
  launchpoolId: number;
  onClose: () => void;
}

const SelectActionModal = ({ launchpoolId, onClose }: Props) => {
  const [selected, setSelected] = useState<SelectActionType | undefined>(undefined);

  const { walletsAtom } = useBunja(walletsBunja);
  const wallet = useAtomValue(walletsAtom);
  const { openWalletModal } = useWalletModal();
  const { currenciesKeyByCurrency } = useV1Currencies();
  const currentLaunchpool = useLaunchpoolById(launchpoolId);

  const stakingCurrency = currentLaunchpool?.stakingCurrency || 'unknown';
  const availableAmount = wallet?.[stakingCurrency]?.walletBalanceExclLocked;
  const stakingCurrencyPrecision = currenciesKeyByCurrency?.[stakingCurrency]?.blockchainInfo[0]?.withdrawalPrecision;

  const sortedCurrenciesByAmount = Object.entries(wallet || {})
    .filter(([currency]) => currency !== stakingCurrency) // 현재 currency는 제외함
    .sort(([, a], [, b]) => {
      return Num(b.walletBalanceWithdrawableInUsdt).minus(a.walletBalanceWithdrawableInUsdt).toNumber();
    })
    .map(([currency]) => currency);
  const largestAmountCurrency = sortedCurrenciesByAmount.length > 0 ? sortedCurrenciesByAmount[0] : 'USDT';

  const handleClickClose = () => {
    onClose();
    setSelected(undefined);
  };

  const onClickContinue = () => {
    if (!selected) return;
    handleClickClose();
    openWalletModal(
      selected,
      selected === 'convert'
        ? {
            defaultCurrency: largestAmountCurrency,
            defaultToCurrency: stakingCurrency,
          }
        : undefined,
    );
  };

  return (
    <Modal.Root size='small' onClose={handleClickClose}>
      <Modal.Header>
        <Modal.Title label={`Add ${stakingCurrency}`} />
      </Modal.Header>
      <Modal.Content>
        <p className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-3', mb: 16 })}>
          Available:{' '}
          <Formatted.Number value={availableAmount} suffix={stakingCurrency} precision={stakingCurrencyPrecision} />
        </p>
        <fieldset
          className={vstack({
            gap: 16,
            alignItems: 'flex-start',
          })}
        >
          {ActionRadioItems.map((item) => (
            <RadioButton
              key={item.value}
              value={item.value}
              checked={selected === item.value}
              onChange={() => setSelected(item.value)}
              css={{ textStyle: 'Text/L/Regular', color: 'content.default.level-4', alignItems: 'center' }}
            >
              {item.label}
            </RadioButton>
          ))}
        </fieldset>
      </Modal.Content>
      <Modal.Footer>
        <Button
          size='large'
          hierarchy='primary'
          label='Continue'
          color='accent'
          disabled={!selected}
          onClick={onClickContinue}
        />
      </Modal.Footer>
    </Modal.Root>
  );
};

export default SelectActionModal;
